/**
 * DetailPageFrame — the one frame for a record's own page.
 *
 * It was the item page's (ItemDetailThirdPassView) and is the same markup,
 * taken out so every record with a page of its own is drawn in it: an item, a
 * commitment, and the next one. Top to bottom:
 *
 *   ‹ Back to <list>                      (keeps the list's filters: pass its href)
 *   identity chips · state · owner        actions (Ask AI, Discuss, Watch, More…)
 *   Title                                 (or the page's own title editor)
 *   one-line description
 *   ┌ standing band: three or four cells, label / value / note ┐
 *   banner
 *   tabs
 *   the tab's content                     │ the rail: "At a glance" groups
 *
 * The frame holds no data and no state. A page brings its own model, its own
 * editors and its own tab panels; the frame only places them, so two records'
 * pages cannot drift apart in layout.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tabs } from '@/components/ds';
import { LABEL_MONO, panel, panelInset } from '@/components/mono/primitives';

export interface StandingCell {
  label: string;
  value: ReactNode;
  note?: ReactNode;
}

export interface DetailTab<Id extends string = string> {
  id: Id;
  label: string;
  count?: number;
}

export interface DetailPageFrameProps<Id extends string = string> {
  /** "‹ Back to items". Pass the list's URL so its filters come back with it. */
  back: { label: string; href?: string };
  /** Left of the header row: kind badge, state chip, owner, version. */
  identity: ReactNode;
  /** Right of the header row: the page's buttons and menus. */
  actions?: ReactNode;
  /** The record's title. Ignored when `headline` is given. */
  title?: ReactNode;
  /** Beside the title (an Edit button, source marks). */
  titleAside?: ReactNode;
  /** Under the title. */
  description?: ReactNode;
  /** Replaces title and description: the page's own title block or its editor. */
  headline?: ReactNode;
  /** Where the record stands, at a glance across the top. */
  standing?: StandingCell[];
  /** Under the standing band (a plan-of-record band, a lineage line). */
  banner?: ReactNode;
  tabs: DetailTab<Id>[];
  activeTab: Id;
  onTabChange: (id: Id) => void;
  tabsLabel: string;
  /** The right-hand column. */
  rail?: ReactNode;
  /** The tab panels. */
  children: ReactNode;
  className?: string;
}

export function DetailPageFrame<Id extends string = string>({
  back,
  identity,
  actions,
  title,
  titleAside,
  description,
  headline,
  standing,
  banner,
  tabs,
  activeTab,
  onTabChange,
  tabsLabel,
  rail,
  children,
  className = 'mt-6',
}: DetailPageFrameProps<Id>) {
  return (
    <div {...panel(`${className} px-4 py-5 md:px-7`.trim())}>
      <div className={LABEL_MONO}>{back.href ? <Link to={back.href}>‹ {back.label}</Link> : `‹ ${back.label}`}</div>

      {/* Persistent header: identity, responsibility, standing */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">{identity}</div>
        {actions && <div className="relative flex flex-wrap justify-end gap-2">{actions}</div>}
      </div>

      {headline ?? (
        <div>
          <h2 className="mt-4 text-[26px] font-semibold leading-tight" style={{ color: 'var(--text-pri)' }}>
            {title} {titleAside}
          </h2>
          {description != null && <p className="mt-2 max-w-[84ch] text-[var(--text-sec)]">{description}</p>}
        </div>
      )}

      {standing && standing.length > 0 && (
        <div {...panelInset('mt-4 flex flex-wrap gap-x-6 gap-y-3 px-4 py-3')} aria-label="Standing">
          {standing.map((s) => (
            <div key={s.label} className="min-w-0">
              <div className={LABEL_MONO}>{s.label}</div>
              <div className="font-semibold text-[var(--text-pri)]">{s.value}</div>
              {s.note != null && <div className="text-[13.5px] text-[var(--text-dim)]">{s.note}</div>}
            </div>
          ))}
        </div>
      )}
      {banner}

      <Tabs<Id>
        className="mt-5"
        ariaLabel={tabsLabel}
        active={activeTab}
        onChange={onTabChange}
        groups={[{ tabs: tabs.map((t) => ({ id: t.id, label: t.label, count: t.count })) }]}
      />

      <div className="mt-5 flex flex-col gap-10 lg:flex-row">
        <div className="min-w-0 flex-1">{children}</div>
        {rail != null && <aside className="min-w-0 lg:w-[260px] lg:shrink-0">{rail}</aside>}
      </div>
    </div>
  );
}

// ── The rail ─────────────────────────────────────────────────────────────────

export interface DetailRailRow {
  k: string;
  v: ReactNode;
  /** Makes the value a button: open its editor, or go to where it is kept. */
  onClick?: () => void;
  /** Set when the row opens an editor in place: whether it is open now. */
  open?: boolean;
  /** Drawn under the row while `open`. */
  editor?: ReactNode;
}

export interface DetailRailGroup {
  h: string;
  rows: DetailRailRow[];
}

/** "At a glance": groups of label / value rows down the right of the page. */
export function DetailRail({ groups, footnote }: { groups: DetailRailGroup[]; footnote?: ReactNode }) {
  return (
    <>
      {groups.map((g) => (
        <div key={g.h} className="mb-5">
          <div className={`${LABEL_MONO} mb-1.5`}>{g.h}</div>
          <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-2.5 gap-y-1.5 text-sm">
            {g.rows.map((r) => (
              <div key={r.k} className="contents">
                <dt className="text-[var(--text-dim)]">{r.k}</dt>
                <dd className="min-w-0 font-medium text-[var(--text-pri)]">
                  {r.onClick ? (
                    <button
                      type="button"
                      className="text-left underline decoration-[var(--rule-bright)] decoration-dotted underline-offset-[3px] hover:decoration-solid"
                      onClick={r.onClick}
                      aria-expanded={r.open}
                    >
                      {r.v}
                    </button>
                  ) : (
                    r.v
                  )}
                </dd>
                {r.open && r.editor && <div className="col-span-2 py-2">{r.editor}</div>}
              </div>
            ))}
          </dl>
        </div>
      ))}
      {footnote}
    </>
  );
}

